import { useQuery } from "@tanstack/react-query";
import { createCatalogClient, createOrdersClient, createPaymentsClient } from "@tcg/api-client";
import { CircleAlert } from "lucide-react";
import { type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";

import { Money, type MoneyValue } from "@/components/Money";
import { unwrap } from "@/lib/api/errors";
import { useServiceClient } from "@/lib/api/use-api";
import { usePermissions } from "@/lib/auth/usePermissions";
import { cn } from "@/lib/utils";

import { type Period, PERIODS, periodRange, periodStart } from "./periods";
import { DASHBOARD_REFRESH_MS } from "./useTodoSources";

/** Channels of phase 1 (R18): the website and the shop register — no marketplace numbers. */
export const KPI_CHANNELS = ["web", "pos"] as const;
type KpiChannel = (typeof KPI_CHANNELS)[number];

const common = {
  meta: { errorToast: false },
  refetchInterval: DASHBOARD_REFRESH_MS,
  refetchIntervalInBackground: false,
  staleTime: 20_000,
} as const;

function Card({
  title,
  loading,
  error,
  onRetry,
  children,
  slot,
}: {
  title: string;
  loading: boolean;
  error: boolean;
  onRetry: () => void;
  children: ReactNode;
  slot: string;
}) {
  const { t } = useTranslation("dashboard");
  return (
    <li
      data-kpi={slot}
      className="flex min-h-28 flex-col gap-2 rounded-lg border p-4"
      aria-busy={loading}
    >
      <h3 className="text-sm font-medium text-muted-foreground">{title}</h3>
      {loading ? (
        <span aria-hidden className="h-8 w-2/3 animate-pulse rounded bg-muted" />
      ) : error ? (
        <span
          role="alert"
          className="flex flex-wrap items-center gap-2 text-sm text-status-problem"
        >
          <CircleAlert className="size-4" aria-hidden />
          {t("kpi.error")}
          <button
            type="button"
            onClick={onRetry}
            className="min-h-touch rounded-md border px-3 text-sm font-medium text-foreground hover:bg-accent"
          >
            {t("todo.retry")}
          </button>
        </span>
      ) : (
        children
      )}
    </li>
  );
}

/** `GET /v1/admin/reports/payment-fees?groupBy=channel` (JSON form of the report). */
interface FeeReport {
  rows: Array<{ key: Record<string, string | null>; gross: MoneyValue; count: number }>;
}

interface ChannelSales {
  gross: MoneyValue | null;
  count: number;
}

/**
 * KPI (T11-10): money received (税込, from payment-service's report per channel — web and shop
 * register only), number of orders and cards added in the chosen period. Every number comes from
 * an API; nothing is added up here.
 */
export function KpiCards({ now = () => new Date() }: { now?: () => Date }) {
  const { t } = useTranslation("dashboard");
  const perms = usePermissions();
  const [period, setPeriod] = useState<Period>("today");
  const payments = useServiceClient(createPaymentsClient);
  const orders = useServiceClient(createOrdersClient);
  const catalog = useServiceClient(createCatalogClient);
  const range = periodRange(period, now());

  const sales = useQuery({
    ...common,
    queryKey: ["dashboard", "kpi", "sales", range.from, range.to],
    enabled: perms.can("payments.reconcile"),
    queryFn: async ({ signal }): Promise<Record<KpiChannel, ChannelSales>> => {
      const report = (await unwrap(
        payments.GET("/v1/admin/reports/payment-fees", {
          params: { query: { from: range.from, to: range.to, groupBy: "channel", format: "json" } },
          signal,
        }),
      )) as FeeReport;
      const out: Record<KpiChannel, ChannelSales> = {
        web: { gross: null, count: 0 },
        pos: { gross: null, count: 0 },
      };
      for (const row of report.rows) {
        const channel = row.key.channel;
        if (channel === "web" || channel === "pos") {
          out[channel] = { gross: row.gross, count: row.count };
        }
      }
      return out;
    },
  });

  const orderCount = useQuery({
    ...common,
    queryKey: ["dashboard", "kpi", "orders", range.from],
    enabled: perms.can("orders.read"),
    queryFn: async ({ signal }) => {
      const page = await unwrap(
        orders.GET("/v1/admin/orders", {
          params: { query: { "filter[placedFrom]": range.from, limit: 200 } },
          signal,
        }),
      );
      return { n: page.data.length, more: page.pageInfo.hasNextPage };
    },
  });

  const newCards = useQuery({
    ...common,
    queryKey: ["dashboard", "kpi", "new-cards", range.from],
    enabled: perms.can("products.read"),
    queryFn: async ({ signal }) => {
      const page = await unwrap(
        catalog.GET("/v1/admin/products", {
          params: { query: { sort: "created_desc", limit: 100 } },
          signal,
        }),
      );
      const start = periodStart(period, now()).getTime();
      const added = page.data.filter((row) => Date.parse(row.createdAt) >= start);
      return {
        n: added.length,
        more: added.length === page.data.length && page.pageInfo.hasNextPage,
      };
    },
  });

  const cards: ReactNode[] = [];
  if (perms.can("payments.reconcile")) {
    for (const channel of KPI_CHANNELS) {
      const value = sales.data?.[channel];
      cards.push(
        <Card
          key={`sales-${channel}`}
          slot={`sales-${channel}`}
          title={t(`kpi.sales_${channel}`)}
          loading={sales.isPending}
          error={sales.isError}
          onRetry={() => void sales.refetch()}
        >
          <span className="text-2xl font-bold">
            {value?.gross ? <Money value={value.gross} /> : <Money value="0" />}
          </span>
          <span className="text-xs text-muted-foreground">
            {t("kpi.payments_count", { count: value?.count ?? 0 })}
          </span>
        </Card>,
      );
    }
  }
  if (perms.can("orders.read")) {
    cards.push(
      <Card
        key="orders"
        slot="orders"
        title={t("kpi.orders")}
        loading={orderCount.isPending}
        error={orderCount.isError}
        onRetry={() => void orderCount.refetch()}
      >
        <span className="text-2xl font-bold">
          {orderCount.data?.n ?? 0}
          {orderCount.data?.more ? "+" : ""}
        </span>
      </Card>,
    );
  }
  if (perms.can("products.read")) {
    cards.push(
      <Card
        key="new-cards"
        slot="new-cards"
        title={t("kpi.new_cards")}
        loading={newCards.isPending}
        error={newCards.isError}
        onRetry={() => void newCards.refetch()}
      >
        <span className="text-2xl font-bold">
          {newCards.data?.n ?? 0}
          {newCards.data?.more ? "+" : ""}
        </span>
      </Card>,
    );
  }
  if (cards.length === 0) {
    return null;
  }

  return (
    <section aria-labelledby="kpi-title" data-slot="kpi" className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 id="kpi-title" className="text-lg font-bold">
          {t("kpi.title")}
        </h2>
        <div
          role="radiogroup"
          aria-label={t("kpi.period")}
          className="inline-flex rounded-md border p-0.5"
        >
          {PERIODS.map((p) => (
            <button
              key={p}
              type="button"
              role="radio"
              aria-checked={period === p}
              onClick={() => {
                setPeriod(p);
              }}
              className={cn(
                "min-h-touch rounded px-3 text-sm font-medium",
                period === p ? "bg-foreground text-background" : "hover:bg-accent",
              )}
            >
              {t(`kpi.periods.${p}`)}
            </button>
          ))}
        </div>
      </div>
      <ul className="grid grid-cols-2 gap-3 xl:grid-cols-4">{cards}</ul>
      {perms.can("payments.reconcile") ? (
        <p className="text-xs text-muted-foreground">{t("kpi.sales_note")}</p>
      ) : null}
    </section>
  );
}
